import { createRecordingGateway, type RecordingOptions } from '@/utils/recording-gateway';

import type { AuthUser, Licence, OtpChallenge } from '../domain/login.model';
import type { LoginGateway } from './login.gateway';

/** A minimal stub user returned by verify/trusted-device unless overridden. */
const STUB_USER: AuthUser = {
  id: '11111111-1111-1111-1111-111111111111',
  email: 'jane@acme.co',
  first_name: 'Jane',
  last_name: 'Doe',
  role: 'user',
  is_admin: false,
  admin_role: null,
  licence_id: '22222222-2222-2222-2222-222222222222',
  subscription_date: '2026-01-01',
  subscription_status: 'active',
  last_login_date: null,
  avatar_url: null,
};

/** A minimal stub licence returned by fetchCurrentLicence unless overridden. */
const STUB_LICENCE: Licence = {
  id: '22222222-2222-2222-2222-222222222222',
  name: 'Acme Capital',
  owner_id: null,
  opening_date: '2026-01-01',
  status: 'active',
  user_count: 1,
  created_at: '2026-01-01T00:00:00.000Z',
  updated_at: '2026-01-01T00:00:00.000Z',
};

/** Default step-1 response: the 2FA branch (an OTP challenge). */
const DEFAULT_OTP_CHALLENGE: OtpChallenge = {
  otp_token: 'otp-token-stub',
  email: STUB_USER.email,
};

interface InMemoryOptions extends RecordingOptions<LoginGateway> {
  /** Step-1 result. Default = OTP challenge; pass `{ user }` for a trusted device. */
  authenticate?: { user: AuthUser } | OtpChallenge;
  /** Step-2 result. */
  verifyOtp?: { user: AuthUser };
  /** The licence returned by fetchCurrentLicence. */
  licence?: Licence;
}

/**
 * Deterministic in-memory gateway for tests. Records every call and lets a test
 * force failures or hangs per method (via the shared `createRecordingGateway`),
 * or pick the trusted-device vs OTP-required branch, so every saga path runs
 * without a network.
 */
export function createInMemoryGateway(options: InMemoryOptions = {}) {
  return createRecordingGateway<LoginGateway>(
    {
      authenticate: () => options.authenticate ?? DEFAULT_OTP_CHALLENGE,
      verifyOtp: () => options.verifyOtp ?? { user: STUB_USER },
      resendOtp: () => undefined,
      fetchCurrentLicence: () => options.licence ?? STUB_LICENCE,
    },
    options,
  );
}

export { STUB_LICENCE,STUB_USER };
